import { router } from 'expo-router';
import { useEffect, useState, type ReactNode } from 'react';
import { StyleSheet } from 'react-native';

import { useToast } from '@/src/components/Toast';
import {
  CommentThreadRow,
  ThreadedCommentList,
} from '@/src/features/comments/CommentThreadRow';
import type {
  CommentReplyTarget,
  CommentSortMode,
} from '@/src/features/comments/comment-threading';
import {
  EditContentModal,
  EditHistoryModal,
} from '@/src/features/posts/ContentEditModals';
import {
  applyReactionTransition,
  createReactionMutationId,
  shouldClearOptimisticReaction,
} from '@/src/features/posts/ReactionControls';
import {
  emptyReactionCounts,
  type ProfileMention,
  type Reaction,
  type ReactionCounts,
} from '@/src/features/posts/post-service';
import { spacing } from '@/src/theme/spacing';
import { openUserProfile } from '@/src/utils/profile-navigation';

import {
  loadGarageVehicleCommentEditHistory,
  MAX_GARAGE_COMMENT_LENGTH,
  type GarageVehicle,
  type GarageVehicleComment,
} from './garage-service';
import {
  useDeleteGarageVehicleComment,
  useEditGarageVehicleComment,
  useGarageVehicleCommentReaction,
  useSetGarageVehicleCommentReaction,
} from './use-garage';

type OptimisticGarageCommentReactionState = {
  baseCounts: ReactionCounts;
  baseReaction: Reaction | null;
  mutationId: string;
  reaction: Reaction | null;
};

function GarageVehicleCommentThreadRow({
  comment,
  currentUid,
  isReply = false,
  onReply,
  vehicle,
}: {
  comment: GarageVehicleComment;
  currentUid?: string;
  isReply?: boolean;
  onReply?: (target: CommentReplyTarget) => void;
  vehicle: GarageVehicle;
}) {
  const { showError, showSuccess } = useToast();
  const reactionState = useGarageVehicleCommentReaction({
    commentId: comment.id,
    uid: currentUid,
    vehicleId: vehicle.id,
  });
  const setReaction = useSetGarageVehicleCommentReaction();
  const deleteComment = useDeleteGarageVehicleComment();
  const editComment = useEditGarageVehicleComment();
  const [optimisticReaction, setOptimisticReaction] =
    useState<OptimisticGarageCommentReactionState | null>(null);
  const [editingComment, setEditingComment] = useState(false);
  const [showEditHistory, setShowEditHistory] = useState(false);
  const canDelete =
    currentUid === comment.authorUid || currentUid === vehicle.ownerUid;
  const canEdit = currentUid === comment.authorUid;
  const hasEditHistory = (comment.editCount ?? 0) > 0;
  const baseReactionCounts = comment.reactionCounts ?? emptyReactionCounts();
  const optimisticCounts = optimisticReaction
    ? applyReactionTransition(optimisticReaction)
    : null;
  const visibleReaction = optimisticReaction
    ? optimisticReaction.reaction
    : reactionState.reaction;
  const visibleReactionCounts = optimisticCounts?.counts ?? baseReactionCounts;

  useEffect(() => {
    setOptimisticReaction(null);
  }, [comment.id, currentUid]);

  useEffect(() => {
    if (!optimisticReaction) {
      return;
    }

    if (
      optimisticCounts &&
      shouldClearOptimisticReaction({
        currentCounts: baseReactionCounts,
        currentMutationAck: reactionState.mutationId,
        expectedCounts: optimisticCounts.counts,
        startingCounts: optimisticReaction.baseCounts,
        persistedReaction: reactionState.reaction,
        targetMutationId: optimisticReaction.mutationId,
        targetReaction: optimisticReaction.reaction,
      })
    ) {
      setOptimisticReaction(null);
    }
  }, [
    baseReactionCounts,
    optimisticCounts,
    optimisticReaction,
    reactionState.mutationId,
    reactionState.reaction,
  ]);

  function selectReaction(reaction: Reaction) {
    if (!currentUid) {
      router.push('/sign-in');
      return;
    }

    if (reactionState.loading) {
      return;
    }

    const nextReaction = visibleReaction === reaction ? null : reaction;
    const mutationId = createReactionMutationId();

    setOptimisticReaction({
      baseCounts: optimisticCounts?.counts ?? baseReactionCounts,
      baseReaction: visibleReaction,
      mutationId,
      reaction: nextReaction,
    });

    setReaction.mutate(
      {
        commentId: comment.id,
        mutationId,
        reaction: nextReaction,
        vehicleId: vehicle.id,
      },
      {
        onError: (error) => {
          setOptimisticReaction(null);
          showError(error, 'Unable to update that comment reaction.');
        },
      },
    );
  }

  function openCommentAuthorProfile() {
    if (!currentUid) {
      router.push('/sign-in');
      return;
    }

    openUserProfile({ currentUid, targetUid: comment.authorUid });
  }

  function deleteGarageComment() {
    deleteComment.mutate(
      { commentId: comment.id, vehicleId: vehicle.id },
      {
        onError: (error) => {
          showError(error, 'Unable to delete that comment.');
        },
        onSuccess: () => {
          showSuccess('Comment deleted.', 'Garage updated');
        },
      },
    );
  }

  async function saveGarageCommentEdit(
    nextBody: string,
    nextMentions: ProfileMention[],
  ) {
    try {
      await editComment.mutateAsync({
        body: nextBody,
        commentId: comment.id,
        profileMentions: nextMentions,
        vehicleId: vehicle.id,
      });
    } catch (error) {
      showError(error, 'Unable to update that comment.');
      return;
    }

    setEditingComment(false);
    showSuccess('Your comment was updated.', 'Comment saved');
  }

  return (
    <>
      <CommentThreadRow
        canDelete={canDelete}
        canEdit={canEdit}
        comment={comment}
        currentUid={currentUid}
        deleteDisabled={deleteComment.isPending}
        editDisabled={editComment.isPending}
        isReply={isReply}
        labels={{
          gifAccessibilityLabel: 'Garage comment GIF',
          imageAccessibilityLabel: 'Garage comment image',
          linkPreviewSource: 'garage_comment_link_preview',
          mediaAnalyticsContext: 'garage_vehicle',
          mentionSource: 'garage_comment_mention',
          photoViewerTitle: 'Garage comment photo',
        }}
        reactionCounts={visibleReactionCounts}
        reactionDisabled={reactionState.loading}
        reactionLoading={false}
        reactionTarget={{
          commentId: comment.id,
          kind: 'vehicle_comment',
          vehicleId: vehicle.id,
        }}
        selectedReaction={visibleReaction}
        showEditHistory={hasEditHistory && Boolean(currentUid)}
        onAuthorPress={openCommentAuthorProfile}
        onDelete={deleteGarageComment}
        onEdit={() => setEditingComment(true)}
        onReply={onReply}
        onSelectReaction={selectReaction}
        onShowEditHistory={() => setShowEditHistory(true)}
      />
      <EditContentModal
        allowEmpty={Boolean(comment.media)}
        body={comment.body ?? ''}
        loading={editComment.isPending}
        maxLength={MAX_GARAGE_COMMENT_LENGTH}
        mentions={comment.profileMentions ?? []}
        scope={{ kind: 'vehicle_comment', vehicleId: vehicle.id }}
        title="Edit comment"
        visible={editingComment}
        onClose={() => {
          if (!editComment.isPending) {
            setEditingComment(false);
          }
        }}
        onSave={(nextBody, nextMentions) => {
          void saveGarageCommentEdit(nextBody, nextMentions);
        }}
      />
      <EditHistoryModal
        currentUid={currentUid}
        loadHistory={() =>
          loadGarageVehicleCommentEditHistory({
            commentId: comment.id,
            vehicleId: vehicle.id,
          })
        }
        title="Comment edit history"
        visible={showEditHistory}
        onClose={() => setShowEditHistory(false)}
      />
    </>
  );
}

export function GarageVehicleCommentThreadList({
  comments,
  currentUid,
  onReply,
  orderSessionKey,
  replyComposer,
  replyTarget,
  sortMode,
  vehicle,
}: {
  comments: GarageVehicleComment[];
  currentUid?: string;
  onReply?: (target: CommentReplyTarget) => void;
  orderSessionKey?: boolean | number | string | null;
  replyComposer?: ReactNode;
  replyTarget?: CommentReplyTarget | null;
  sortMode?: CommentSortMode;
  vehicle: GarageVehicle;
}) {
  return (
    <ThreadedCommentList
      comments={comments}
      groupStyle={styles.group}
      orderSessionKey={orderSessionKey}
      replyComposer={replyComposer}
      replyTarget={replyTarget}
      sortMode={sortMode}
      renderComment={(comment, isReply) => (
        <GarageVehicleCommentThreadRow
          comment={comment}
          currentUid={currentUid}
          isReply={isReply}
          vehicle={vehicle}
          onReply={onReply}
        />
      )}
    />
  );
}

const styles = StyleSheet.create({
  group: {
    gap: spacing.sm,
  },
});
